import { useEffect, useRef, useState } from 'react';
import { isDefined } from './objectUtils';

export type MaybeAsyncFunction<T> = (() => T) | (() => Promise<T>);

type Primitive = number | string | boolean;
export type LoadableStatePayload<T> = T extends Primitive ? { value: T } : T;

export type LoadableStateLoading = { isLoaded: false; isLoading: true; hasError: false; hasValue: false };
export type LoadableStateLoadingWithValue<T> = LoadableStatePayload<T> & {
  isLoaded: false;
  isLoading: true;
  hasError: false;
  hasValue: true;
};
export type LoadableStateLoaded<T> = LoadableStatePayload<T> & { isLoaded: true; isLoading: false; hasError: false };
export type LoadableStateWithValue<T> = LoadableStateLoadingWithValue<T> | LoadableStateLoaded<T>;
export type LoadableStateFailed = { isLoaded: false; isLoading: false; hasError: true };
export type LoadableStateUninitialized = { isLoaded: false; isLoading: false; hasError: false };

export type LoadableState<T> =
  | LoadableStateLoaded<T>
  | LoadableStateLoading
  | LoadableStateLoadingWithValue<T>
  | LoadableStateFailed
  | LoadableStateUninitialized;

function isPrimitive(value: unknown): value is Primitive {
  return typeof value === 'number' || typeof value === 'string' || typeof value === 'boolean';
}

// If you want to use more strictly typed parameters, add another overload, DON'T use the base variant with any-s.
function merge<A, B, O>(
  loadables: [LoadableState<A>, LoadableState<B>],
  mergeFn: (a: LoadableStateWithValue<A>, b: LoadableStateWithValue<B>) => O,
): LoadableState<O>;

function merge<A, B, C, O>(
  loadables: [LoadableState<A>, LoadableState<B>, LoadableState<C>],
  mergeFn: (a: LoadableStateWithValue<A>, b: LoadableStateWithValue<B>, c: LoadableStateWithValue<C>) => O,
): LoadableState<O>;

function merge(
  loadables: LoadableState<unknown>[],
  mergeFn: (...loaded: LoadableStateWithValue<unknown>[]) => unknown,
): LoadableState<any> {
  let state: 'failed' | 'loading' | undefined = undefined;
  let eachHasValue = true;
  for (const loadable of loadables) {
    // If there is an uninitialized loadable, loading did not yet occur.
    // As a best effort we treat it as loading in the merged LoadableState.
    if (Loadable.isUninitialized(loadable)) {
      state = 'loading';
      eachHasValue = false;
      break;
    }
    // If there is an error, no reason to check further
    if (loadable.hasError) {
      state = 'failed';
      eachHasValue = false;
      break;
    }
    // If there is a loading loadable...
    if (loadable.isLoading) {
      state = 'loading';
      // ...and it has no value no reason to check further
      if (!loadable.hasValue) {
        eachHasValue = false;
        break;
      }
    }
  }
  // If either loadable failed, return failed
  if (state === 'failed') {
    return Loadable.failed;
  }
  try {
    // If either loadable is loading, only merge them if each has a value
    if (state === 'loading') {
      return eachHasValue
        ? Loadable.loadingWith(mergeFn(...(loadables as LoadableStateWithValue<unknown>[])))
        : Loadable.loading;
    }
    // All loadables are loaded and have a value
    return Loadable.loaded(mergeFn(...(loadables as LoadableStateWithValue<unknown>[])));
  } catch (e) {
    return Loadable.failed;
  }
}

function map<I, O>(loadable: LoadableState<I>, transform: (value: LoadableStateWithValue<I>) => O): LoadableState<O> {
  if (loadable.isLoaded) {
    return Loadable.loaded(transform(loadable));
  }
  if (loadable.isLoading && loadable.hasValue) {
    return Loadable.loadingWith(transform(loadable));
  }
  return loadable;
}

function getOrElse<T>(loadable: LoadableState<T>, defaultValue: T): T {
  if (Loadable.hasValue(loadable)) {
    // Primitives are wrapped in a { value: T } construct. It's not straight forward to figure out at runtime
    // if the Loadable is wrapping a primitive value, or is an object having a value field.
    // For now it's being checked using defaultValue.
    if (isPrimitive(defaultValue)) {
      return (loadable as LoadableStatePayload<Primitive>).value as T;
    }
    return loadable as T;
  }
  return defaultValue;
}

export const Loadable = {
  get loading() {
    return { isLoaded: false, isLoading: true, hasValue: false } as LoadableStateLoading;
  },
  get failed() {
    return {
      isLoaded: false,
      isLoading: false,
      hasError: true,
    } as LoadableStateFailed;
  },
  get uninitialized() {
    return {
      isLoaded: false,
      isLoading: false,
      hasError: false,
    } as LoadableStateUninitialized;
  },
  loaded: <T>(value: T): LoadableState<T> => {
    if (isPrimitive(value)) {
      return { hasError: false, isLoaded: true, isLoading: false, value } as LoadableStateLoaded<Primitive>;
    } else {
      if (Array.isArray(value)) {
        (value as unknown as LoadableStateLoaded<T>).hasError = false;
        (value as unknown as LoadableStateLoaded<T>).isLoaded = true;
        (value as unknown as LoadableStateLoaded<T>).isLoading = false;
        return value as LoadableStateLoaded<T>;
      } else {
        return {
          ...value,
          hasError: false,
          isLoaded: true,
          isLoading: false,
        };
      }
    }
  },
  loadingWith: <T>(value: T): LoadableState<T> => {
    if (isPrimitive(value)) {
      return {
        isLoaded: false,
        isLoading: true,
        hasError: false,
        hasValue: true,
        value,
      } as LoadableStateLoadingWithValue<Primitive>;
    } else {
      if (Array.isArray(value)) {
        (value as unknown as LoadableStateLoadingWithValue<T>).isLoaded = false;
        (value as unknown as LoadableStateLoadingWithValue<T>).isLoading = true;
        (value as unknown as LoadableStateLoadingWithValue<T>).hasError = false;
        (value as unknown as LoadableStateLoadingWithValue<T>).hasValue = true;
        return value as LoadableStateLoadingWithValue<T>;
      } else {
        return {
          ...value,
          isLoaded: false,
          isLoading: true,
          hasError: false,
          hasValue: true,
        } as LoadableStateLoadingWithValue<T>;
      }
    }
  },
  map,
  merge,
  getOrElse,
  hasValue: <T>(loadable: LoadableState<T>): loadable is LoadableStateWithValue<T> => {
    return loadable.isLoaded || (loadable.isLoading && loadable.hasValue);
  },
  isUninitialized: (loadable: LoadableState<unknown>): loadable is LoadableStateUninitialized => {
    return !loadable.isLoaded && !loadable.isLoading && !loadable.hasError;
  },
};

/**
 * This hook gives you a special state, which value is a promise result.
 * During the promise is pending, the value will be { isLoading: true }
 *
 * When the promise resolved value is a number or string the resolved value will be wrapped in an object with the key `value`. See example below.
 *
 * For example:
 *
 * @example
 *
 *   const convertToCurrency = 'HUF';
 *   ...
 *   const balance = useLoadableState(() => fetchBalance(convertToCurrency), [convertToCurrency]);
 *   const loggedInUser = useLoadableState(() => fetchLoggedInUser(), []);
 *   ...
 *   <div>
 *     {loggedInUser.isLoaded && <span>{loggedInUser.name} balance is</span>}
 *     {balance.isLoading && <Loader />}
 *     {!balance.isLoading && <span>{balance.value}</span>}
 *   </div>
 *
 * @param fn Async function to calculate the value
 * @param deps Dependencies to recalculate the value
 */
export const useLoadableState = <T>(fn: () => Promise<T | undefined>, deps: Array<unknown> = []): LoadableState<T> => {
  const [value, setValue] = useState<LoadableState<T>>({
    isLoading: true,
    isLoaded: false,
    hasError: false,
    hasValue: false,
  });

  useEffect(() => {
    setValue({
      isLoading: true,
      isLoaded: false,
      hasError: false,
      hasValue: false,
    });
    fn()
      .then(res => {
        if (isDefined(res)) {
          if (
            res &&
            typeof res === 'object' &&
            res.hasOwnProperty('isLoading') &&
            res.hasOwnProperty('isLoaded') &&
            res.hasOwnProperty('hasError')
          ) {
            // if the result is a LoadableState, set it directly
            setValue(res as LoadableState<T>);
          } else {
            const meta = { isLoading: false, isLoaded: true, hasError: false };
            if (typeof res === 'number' || typeof res === 'string') {
              setValue({
                ...meta,
                value: res,
              } as LoadableStateLoaded<number | string>);
            } else if (typeof res === 'object' && Array.isArray(res)) {
              const o = res as any;
              o.isLoading = false;
              o.isLoaded = true;
              o.hasError = false;
              setValue(o as LoadableStateLoaded<T>);
            } else {
              setValue({
                ...res,
                ...meta,
              } as LoadableStateLoaded<T>);
            }
          }
        }
      })
      .catch(() => {
        setValue({
          isLoading: false,
          isLoaded: false,
          hasError: true,
        });
      });
    // eslint-disable-next-line
  }, [setValue, ...deps]);

  return value;
};

/**
 * This hooks manages state that a promise is currently pending or not by wrapping the promise call in a function.
 *
 * @returns [boolean, Function, MutableRefObject<boolean>] A tuple with a ref to a boolean that indicates if a promise is pending and a function to start a promise.
 * @example
 *   const [isPending, startLoad, isPendingRef] = useLoadableStateTransition();
 *   const [state, setState] = useState<LoadableState<number>>(Loadable.uninitialized);
 *
 *   //
 *   // This will set isPending state to true when the promise is pending and false when the promise is resolved or rejected.
 *   //
 *   const loadWithUpdatedLoaderState = async () => {
 *     try {
 *       startLoad(async () => {
 *         await asyncFunction();
 *       });
 *     } catch (err) {
 *       showError(err);
 *     }
 *   };
 *
 *   //
 *   // This will set isPending state to true when the promise is pending and false when the promise is resolved or rejected.
 *   // and also prevent the promise to start if it is already pending.
 *   //
 *   const loadWithPreventDoubleClick = async () => {
 *     try {
 *       if (isPendingRef.current) {
 *         return;
 *       }
 *
 *       startLoad(async () => {
 *         setState(Loadable.loading);
 *         setState(Loadable.loaded(await fetchBalance()));
 *       });
 *     } catch {
 *       setState(Loadable.failed);
 *     }
 *   };
 */
export const useLoadableStateTransition = () => {
  const isPendingRef = useRef<boolean>(false);
  const [isPending, setIsPending] = useState(false);

  const startTransition = async <T>(fn: MaybeAsyncFunction<T>): Promise<T> => {
    isPendingRef.current = true;
    setIsPending(true);

    try {
      return await fn();
    } finally {
      isPendingRef.current = false;
      setIsPending(false);
    }
  };

  const startAsyncTransitionOnce = async <T>(fn: MaybeAsyncFunction<T>): Promise<T | undefined> => {
    return isPendingRef.current ? undefined : startTransition(fn);
  };

  return { isPending, startTransition, startAsyncTransitionOnce, isPendingRef };
};
